import { useState, useEffect } from "react";
import cn from "classnames";

// Components
import {
    Box,
    CircularProgress,
    FormControlLabel,
    Grid,
    List,
    ListItemButton,
    ListItemText,
    RadioGroup,
    Skeleton,
    Typography,
} from "@mui/material";
import { RadioButton } from "components/RadioButton";

// Styles
import PermissionsStyles from "./Permissions.styles";

// Utils
import { camelCase, isEmpty } from "lodash";

// Types
import { RoleBundle, RoleBundleResponse } from "services";

export type TSelectedPermissions = {
    ldPermission: RoleBundle;
    pacePermission: RoleBundle;
};

const getGroupedPermissions = (permissionsResponse: RoleBundleResponse) => {
    if (isEmpty(permissionsResponse)) return;

    const { bundles: permissions } = permissionsResponse;

    return [
        {
            title: "LD Permissions",
            permissions: permissions.filter((p) => p.name.includes("LD_")),
        },
        {
            title: "PACE Permissions",
            permissions: permissions.filter((p) => p.name.includes("PACE_")),
        },
    ];
};

const Permissions = ({
    introText,
    isFetching,
    isLoading,
    disabled,
    permissionsBundle,
    selectedPermission,
    onSelectPermission,
}: {
    introText: React.ReactNode;
    isFetching: boolean;
    isLoading?: boolean;
    disabled?: boolean;
    permissionsBundle: RoleBundleResponse;
    selectedPermission: TSelectedPermissions;
    onSelectPermission: (permissions: RoleBundle, type: keyof TSelectedPermissions) => Promise<void>;
}) => {
    const classes = PermissionsStyles();

    const [previousPermission, setPreviousPermission] = useState(selectedPermission);
    const groupedPermissions = getGroupedPermissions(permissionsBundle);

    const handlePermissionChange = async (value: string) => {
        const { bundles: permissions } = permissionsBundle;

        const selectedValue = permissions.filter((p) => p.name === value)[0];
        const type = selectedValue.name.includes("LD") ? "ldPermission" : "pacePermission";

        await onSelectPermission(selectedValue, type);

        setPreviousPermission({
            ...selectedPermission,
            [type]: selectedValue,
        });
    };

    useEffect(() => {
        if (
            isEmpty(selectedPermission.ldPermission) &&
            isEmpty(selectedPermission.pacePermission) &&
            !isEmpty(previousPermission.ldPermission) &&
            !isEmpty(previousPermission.pacePermission)
        ) {
            setPreviousPermission(selectedPermission);
        }
    }, [previousPermission, selectedPermission]);

    const Loader = () => (
        <>
            {Array.from(Array(2)).map((_, i) => (
                <Grid key={i} item xs={6}>
                    <Box mb={2}>
                        <Skeleton animation="wave" sx={{ fontSize: 40 }} />
                        <Skeleton variant="rectangular" animation="wave" height={22} />
                    </Box>
                    <Box mb={2}>
                        <Skeleton animation="wave" sx={{ fontSize: 40 }} />
                        <Skeleton variant="rectangular" animation="wave" height={22} />
                    </Box>
                    <Box mb={2}>
                        <Skeleton animation="wave" sx={{ fontSize: 40 }} />
                        <Skeleton variant="rectangular" animation="wave" height={22} />
                    </Box>
                </Grid>
            ))}
        </>
    );

    return (
        <Box>
            {introText && (
                <Typography variant="subtitle1" className={classes.subtitle}>
                    {introText}
                </Typography>
            )}
            <Grid container spacing={2} mb={2}>
                {!isFetching &&
                    groupedPermissions?.map((permissionGroup) => {
                        const selected =
                            (permissionGroup.title.includes("LD")
                                ? selectedPermission.ldPermission?.name
                                : selectedPermission.pacePermission?.name) ?? "";

                        const wasChanged = permissionGroup.title.includes("LD")
                            ? selectedPermission.ldPermission?.name !== previousPermission.ldPermission?.name
                            : selectedPermission.pacePermission?.name !== previousPermission.pacePermission?.name;

                        return (
                            <Grid item xs={6} key={camelCase(permissionGroup.title)}>
                                <Typography
                                    variant="h6"
                                    className={cn({ [classes.disabled]: disabled })}
                                    sx={{ fontWeight: 600 }}
                                >
                                    {permissionGroup.title}
                                </Typography>
                                <Box>
                                    <RadioGroup
                                        value={selected}
                                        onChange={(ev) => handlePermissionChange(ev.target.value)}
                                    >
                                        <List>
                                            {permissionGroup.permissions.map((permission, i) => (
                                                <ListItemButton
                                                    disabled={disabled}
                                                    key={permission.name}
                                                    className={cn(classes.permissionButton, {
                                                        [classes.selectedPermission]: selected === permission.name,
                                                        [classes.savingPermission]:
                                                            isLoading && wasChanged && selected === permission.name,
                                                    })}
                                                >
                                                    <FormControlLabel
                                                        value={permission.name}
                                                        control={<RadioButton size="small" disabled={disabled} />}
                                                        label={
                                                            <ListItemText
                                                                primary={
                                                                    <Typography variant="h6" sx={{ fontSize: 16 }}>
                                                                        {permission.label}
                                                                    </Typography>
                                                                }
                                                                secondary={
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ fontSize: 14, color: "text.disabled" }}
                                                                    >
                                                                        {permission.description}
                                                                    </Typography>
                                                                }
                                                            />
                                                        }
                                                    />
                                                    {isLoading && wasChanged && selected === permission.name && (
                                                        <CircularProgress color="primary" size={20} />
                                                    )}
                                                </ListItemButton>
                                            ))}
                                        </List>
                                    </RadioGroup>
                                </Box>
                            </Grid>
                        );
                    })}

                {isFetching && <Loader />}
            </Grid>
        </Box>
    );
};

export default Permissions;
